import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import FacetEditor, { FacetPills, FacetValue } from '@/components/FacetEditor'
import EffectRenderer from '@/components/EffectRenderer'
import { EffectData } from '@/lib/magnumMoment'
import { cn } from '@/lib/utils'

interface FacetDefinition {
  key: string
  label: string
  valueType: 'STRING' | 'BOOLEAN' | 'INTEGER' | 'DOUBLE'
  operators: string[]
  suggestions: string[]
}

interface MagnumEventEffect {
  id: number; eventId: number; effectType: string; name: string; config: string | null; sortOrder: number
}
interface ScoredEvent {
  id: number; slug: string; baseProbability: number; effectiveProbability: number; normalizedProbability: number
  facets: FacetValue[]; dialogue: string | null; effects: MagnumEventEffect[]
  specificity: number; cooldownMs: number; maxDeliveries: number | null
}
interface DetailedTestResult {
  matchingEvents: ScoredEvent[]; nearMisses: ScoredEvent[]
  totalProbability: number; nothingProbability: number
}
interface MagnumEvent {
  id: number; slug: string; probability: number; cooldownMs: number; maxDeliveries: number | null
  isActive: boolean; source: string; dialogue: string | null; facets: FacetValue[]; effects: MagnumEventEffect[]
}
interface PersonalityPrompt {
  id: number; version: number; name: string; systemPrompt: string; isActive: boolean
}

const EFFECT_TYPES = ['CONFETTI', 'SCREEN_SHAKE', 'COLOR_INVERT', 'ABSURD_WORD', 'EARLY_RELEASE', 'BIRTHDAY_COUNTDOWN']

type Tab = 'explorer' | 'playground' | 'studio'

export default function MagnumWorkshopPage() {
  const [tab, setTab] = useState<Tab>('explorer')
  const [error, setError] = useState('')

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">&larr; Admin</Link>
        <h1 className="text-2xl font-bold">Tim Workshop</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b">
        {([
          { key: 'explorer' as Tab, label: 'Reaction Explorer' },
          { key: 'playground' as Tab, label: 'Playground' },
          { key: 'studio' as Tab, label: 'Personality Studio' },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition',
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}

      {tab === 'explorer' && <ExplorerTab setError={setError} />}
      {tab === 'playground' && <PlaygroundTab setError={setError} />}
      {tab === 'studio' && <PersonalityStudioTab setError={setError} />}
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────

function getPrevEvents(facets: FacetValue[]): string[] {
  const prev: string[] = []
  for (let i = 1; i <= 3; i++) {
    const f = facets.find(f => f.facetKey === `prev_event_${i}`)
    if (f) prev.push(f.facetValue)
  }
  return prev
}

function formatCooldown(ms: number): string {
  if (ms <= 0) return '—'
  if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`
  if (ms < 3600000) return `${(ms / 60000).toFixed(0)}m`
  return `${(ms / 3600000).toFixed(1)}h`
}

// ─── Distribution Bar Segment ─────────────────────────────────────

function DistBarSegment({ slug, dialogue, pct, color, first, last }: {
  slug: string; dialogue: string | null; pct: number; color: string; first?: boolean; last?: boolean
}) {
  const [hovered, setHovered] = useState(false)
  const segRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const [tipStyle, setTipStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    if (!hovered || !segRef.current || !tipRef.current) return
    const seg = segRef.current.getBoundingClientRect()
    const tip = tipRef.current.getBoundingClientRect()
    const centerX = seg.left + seg.width / 2
    let left = centerX - tip.width / 2
    if (left < 4) left = 4
    if (left + tip.width > window.innerWidth - 4) left = window.innerWidth - 4 - tip.width
    setTipStyle({ position: 'fixed', left, top: seg.top - tip.height - 8 })
  }, [hovered])

  return (
    <div
      ref={segRef}
      className="h-full relative cursor-default"
      style={{
        width: `${pct}%`,
        backgroundColor: color,
        borderRadius: first && last ? '9999px' : first ? '9999px 0 0 9999px' : last ? '0 9999px 9999px 0' : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && (
        <div ref={tipRef} className="z-50 pointer-events-none" style={tipStyle}>
          <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap max-w-xs">
            <div className="font-semibold">{slug}</div>
            <div className="tabular-nums">{pct.toFixed(1)}%</div>
            {dialogue && <div className="text-gray-300 mt-0.5 truncate max-w-[200px]">{dialogue}</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Inline Editable Cell ─────────────────────────────────────────

function InlineInput({ value, onChange, type = 'text', placeholder, className: cls }: {
  value: string; onChange: (val: string) => void; type?: string; placeholder?: string; className?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => { setDraft(value) }, [value])
  useEffect(() => { if (editing) ref.current?.focus() }, [editing])

  if (!editing) {
    return (
      <span
        className={cn('cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1', cls)}
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-muted-foreground">{placeholder || '—'}</span>}
      </span>
    )
  }

  const commit = () => {
    setEditing(false)
    if (draft !== value) onChange(draft)
  }

  return (
    <input
      ref={ref}
      type={type}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false) } }}
      className={cn('rounded border border-blue-300 px-1 py-0 text-sm bg-white w-full', cls)}
      step={type === 'number' ? '0.001' : undefined}
    />
  )
}

function InlineTextarea({ value, onChange, placeholder }: {
  value: string; onChange: (val: string) => void; placeholder?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { setDraft(value) }, [value])
  useEffect(() => { if (editing) ref.current?.focus() }, [editing])

  if (!editing) {
    return (
      <span
        className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 block truncate"
        onClick={() => setEditing(true)}
        title="Click to edit"
      >
        {value || <span className="text-muted-foreground">{placeholder || '—'}</span>}
      </span>
    )
  }

  const commit = () => {
    setEditing(false)
    if (draft !== value) onChange(draft)
  }

  return (
    <textarea
      ref={ref}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      rows={2}
      className="rounded border border-blue-300 px-1 py-0 text-sm bg-white w-full"
    />
  )
}

// ─── Column Filter Dropdown ───────────────────────────────────────

function ColumnFilter({ label, options, value, onChange }: {
  label: string; options: string[]; value: string; onChange: (val: string) => void
}) {
  const hasFilter = value !== ''
  return (
    <div className="flex items-center gap-1">
      <span className="font-medium">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className={cn(
          'text-xs rounded border px-1 py-0.5 bg-white',
          hasFilter && 'border-blue-400 bg-blue-50'
        )}
        title={`Filter ${label}`}
      >
        <option value="">All</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

function TextColumnFilter({ label, value, onChange }: {
  label: string; value: string; onChange: (val: string) => void
}) {
  const hasFilter = value !== ''
  return (
    <div className="flex items-center gap-1">
      <span className="font-medium">{label}</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="..."
        className={cn(
          'text-xs rounded border px-1 py-0.5 w-16',
          hasFilter && 'border-blue-400 bg-blue-50'
        )}
      />
    </div>
  )
}

// ─── Event Explorer ───────────────────────────────────────────────

interface ColumnFilters {
  slug: string
  dialogue: string
  event: string
  prevEvent: string
  cooldown: string
  maxDel: string
  fx: string
}

const emptyFilters: ColumnFilters = { slug: '', dialogue: '', event: '', prevEvent: '', cooldown: '', maxDel: '', fx: '' }

function ExplorerTab({ setError }: { setError: (e: string) => void }) {
  const [definitions, setDefinitions] = useState<FacetDefinition[]>([])
  const [contextValues, setContextValues] = useState<Record<string, string>>({})
  const [result, setResult] = useState<DetailedTestResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<ColumnFilters>(emptyFilters)
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => {
    apiFetch<FacetDefinition[]>('/admin/magnum/facet-definitions').then(setDefinitions).catch(() => {})
  }, [])

  const search = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const contextMap: Record<string, string> = {}
      for (const [key, val] of Object.entries(contextValues)) {
        if (val !== '') contextMap[key] = val
      }
      const res = await apiFetch<DetailedTestResult>('/admin/magnum/engine/test-detailed', {
        method: 'POST',
        body: JSON.stringify(contextMap),
      })
      setResult(res)
    } catch (e: any) {
      setError(e.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }, [contextValues, setError])

  const uniqueEvents = [...new Set(
    result?.matchingEvents.flatMap(e => e.facets.filter(f => f.facetKey === 'event').map(f => f.facetValue)) ?? []
  )].sort()
  const uniquePrevEvents = [...new Set(
    result?.matchingEvents.flatMap(e => getPrevEvents(e.facets)) ?? []
  )].sort()
  const uniqueFx = [...new Set(
    result?.matchingEvents.flatMap(e => e.effects.map(fx => fx.effectType)) ?? []
  )].sort()

  const filteredEvents = result?.matchingEvents.filter(e => {
    if (filters.slug && !e.slug.toLowerCase().includes(filters.slug.toLowerCase())) return false
    if (filters.dialogue && !(e.dialogue && e.dialogue.toLowerCase().includes(filters.dialogue.toLowerCase()))) return false
    if (filters.event) {
      const eventFacet = e.facets.find(f => f.facetKey === 'event')
      if (!eventFacet || eventFacet.facetValue !== filters.event) return false
    }
    if (filters.prevEvent) {
      const prev = getPrevEvents(e.facets)
      if (!prev.includes(filters.prevEvent)) return false
    }
    if (filters.cooldown === 'yes' && e.cooldownMs <= 0) return false
    if (filters.cooldown === 'no' && e.cooldownMs > 0) return false
    if (filters.maxDel === 'yes' && e.maxDeliveries == null) return false
    if (filters.maxDel === 'no' && e.maxDeliveries != null) return false
    if (filters.fx && !e.effects.some(fx => fx.effectType === filters.fx)) return false
    return true
  }) ?? []

  const updateEvent = async (eventId: number, updates: Record<string, any>) => {
    try {
      await apiFetch(`/admin/magnum/events/${eventId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      search()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const deleteEvent = async (eventId: number) => {
    try {
      await apiFetch(`/admin/magnum/events/${eventId}`, { method: 'DELETE' })
      search()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '')

  return (
    <div className="space-y-6">
      {/* Context Builder */}
      <div className="bg-card border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Context</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {definitions.map(def => (
            <ContextInput
              key={def.key}
              definition={def}
              value={contextValues[def.key] ?? ''}
              onChange={(val) => setContextValues(prev => ({ ...prev, [def.key]: val }))}
            />
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={search}
            disabled={loading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button
            onClick={() => { setContextValues({}); setResult(null) }}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Probability Summary */}
      {result && (
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center gap-4 text-sm">
            <span><strong>{result.matchingEvents.length}</strong> matching events</span>
            <span>Total probability: <strong>{result.totalProbability.toFixed(3)}</strong></span>
            <span>Nothing: <strong>{(result.nothingProbability * 100).toFixed(1)}%</strong></span>
          </div>
          <div className="mt-2 pt-10 -mt-8">
            <div className="h-5 bg-gray-100 rounded-full flex relative">
              {result.matchingEvents.map((evt, i) => (
                <DistBarSegment
                  key={evt.id}
                  slug={evt.slug}
                  dialogue={evt.dialogue}
                  pct={evt.normalizedProbability * 100}
                  color={`hsl(${(i * 47) % 360}, 65%, 55%)`}
                  first={i === 0}
                  last={i === result.matchingEvents.length - 1 && result.nothingProbability <= 0}
                />
              ))}
              {result.nothingProbability > 0 && (
                <DistBarSegment
                  slug="nothing"
                  dialogue={null}
                  pct={result.nothingProbability * 100}
                  color="#d1d5db"
                  first={result.matchingEvents.length === 0}
                  last
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {result && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {filteredEvents.length} of {result.matchingEvents.length} reactions
              {contextValues.event && <> for <strong>{contextValues.event}</strong></>}
            </span>
            {hasActiveFilters && (
              <button onClick={() => setFilters(emptyFilters)}
                className="text-xs text-blue-600 hover:text-blue-800">Clear filters</button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-green-600 text-white rounded text-sm font-medium"
            >
              + New Reaction
            </button>
          </div>

          {showCreate && (
            <CreateEventForm
              contextValues={contextValues}
              onCreated={() => { setShowCreate(false); search() }}
              onCancel={() => setShowCreate(false)}
              setError={setError}
            />
          )}

          <div className="bg-card border rounded-lg overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-[140px]" />
                <col className="w-[180px]" />
                <col className="w-[160px]" />
                <col className="w-[80px]" />
                <col className="w-[140px]" />
                <col />
                <col className="w-[40px]" />
              </colgroup>
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="px-3 py-1 font-medium text-xs">Slug</th>
                  <th className="px-3 py-1 font-medium text-xs">Conditions</th>
                  <th className="px-3 py-1 font-medium text-xs">Probability</th>
                  <th className="px-3 py-1 font-medium text-xs">Limits</th>
                  <th className="px-3 py-1 font-medium text-xs">Effects</th>
                  <th className="px-3 py-1 font-medium text-xs">Dialogue</th>
                  <th className="px-3 py-1"></th>
                </tr>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-1">
                    <input type="text" value={filters.slug} onChange={e => setFilters(f => ({ ...f, slug: e.target.value }))}
                      placeholder="Filter slug..." className={cn('w-full text-xs rounded border px-1.5 py-0.5', filters.slug && 'border-blue-400 bg-blue-50')} />
                  </th>
                  <th className="px-3 py-1">
                    <div className="flex gap-1">
                      <select value={filters.prevEvent} onChange={e => setFilters(f => ({ ...f, prevEvent: e.target.value }))}
                        className={cn('flex-1 text-xs rounded border px-1 py-0.5 bg-white', filters.prevEvent && 'border-blue-400 bg-blue-50')}>
                        <option value="">Prev: All</option>
                        {uniquePrevEvents.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </th>
                  <th className="px-3 py-1"></th>
                  <th className="px-3 py-1">
                    <div className="flex gap-1">
                      <select value={filters.cooldown} onChange={e => setFilters(f => ({ ...f, cooldown: e.target.value }))}
                        className={cn('flex-1 text-xs rounded border px-1 py-0.5 bg-white', filters.cooldown && 'border-blue-400 bg-blue-50')}>
                        <option value="">CD</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </th>
                  <th className="px-3 py-1">
                    <select value={filters.fx} onChange={e => setFilters(f => ({ ...f, fx: e.target.value }))}
                      className={cn('w-full text-xs rounded border px-1 py-0.5 bg-white', filters.fx && 'border-blue-400 bg-blue-50')}>
                      <option value="">All FX</option>
                      {uniqueFx.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </th>
                  <th className="px-3 py-1">
                    <input type="text" value={filters.dialogue} onChange={e => setFilters(f => ({ ...f, dialogue: e.target.value }))}
                      placeholder="Filter..." className={cn('w-full text-xs rounded border px-1.5 py-0.5', filters.dialogue && 'border-blue-400 bg-blue-50')} />
                  </th>
                  <th className="px-3 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map(evt => (
                  <InlineEventRow key={evt.id} event={evt} onUpdate={updateEvent} onDelete={deleteEvent} onRefresh={search} setError={setError} />
                ))}
                {filteredEvents.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No matching reactions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Near Misses */}
      {result && result.nearMisses && result.nearMisses.length > 0 && (
        <details className="group">
          <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
            {result.nearMisses.length} near-miss events
            <span className="text-xs ml-1">(would match with more context)</span>
          </summary>
          <div className="mt-2 bg-card border border-dashed rounded-lg overflow-x-auto">
            <table className="w-full text-sm opacity-60">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="px-3 py-2 font-medium">Slug</th>
                  <th className="px-3 py-2 font-medium">Dialogue</th>
                  <th className="px-3 py-2 font-medium">Missing Facets</th>
                  <th className="px-3 py-2 font-medium w-20">Prob</th>
                  <th className="px-3 py-2 font-medium">All Facets</th>
                  <th className="px-3 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {result.nearMisses.map(evt => {
                  const contextKeys = Object.keys(contextValues).filter(k => contextValues[k] !== '')
                  const missingFacets = evt.facets.filter(f => !contextKeys.includes(f.facetKey))
                  return (
                    <tr key={evt.id} className="border-b">
                      <td className="px-3 py-1.5 font-medium">{evt.slug}</td>
                      <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[200px]">{evt.dialogue || '—'}</td>
                      <td className="px-3 py-1.5">
                        {missingFacets.map((f, i) => (
                          <span key={i} className="bg-orange-100 text-orange-700 rounded px-1 py-0.5 text-xs mr-1">
                            {f.facetKey} {f.operator} {f.facetValue}
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{evt.baseProbability.toFixed(3)}</td>
                      <td className="px-3 py-1.5"><FacetPills facets={evt.facets} /></td>
                      <td className="px-3 py-1.5">
                        <button
                          onClick={() => { if (confirm(`Delete "${evt.slug}"?`)) deleteEvent(evt.id) }}
                          className="text-xs text-red-400 hover:text-red-600"
                        >del</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  )
}

function ContextInput({ definition, value, onChange }: {
  definition: FacetDefinition; value: string; onChange: (val: string) => void
}) {
  const { label, valueType, suggestions } = definition

  if (valueType === 'BOOLEAN') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={e => onChange(e.target.checked ? 'true' : '')}
          className="rounded"
        />
        {label}
      </label>
    )
  }

  if (valueType === 'INTEGER' || valueType === 'DOUBLE') {
    return (
      <div>
        <label className="text-xs text-muted-foreground">{label}</label>
        <input
          type="number"
          value={value}
          onChange={e => onChange(e.target.value)}
          step={valueType === 'DOUBLE' ? '0.1' : '1'}
          placeholder={label}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
    )
  }

  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      {suggestions.length > 0 ? (
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm bg-white"
        >
          <option value="">—</option>
          {suggestions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={label}
          className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
        />
      )}
    </div>
  )
}

// ─── Inline Event Row ─────────────────────────────────────────────

const EFFECT_ICONS: Record<string, string> = {
  CONFETTI: '🎉', SCREEN_SHAKE: '📳', COLOR_INVERT: '🔄',
  ABSURD_WORD: '🤪', EARLY_RELEASE: '🎁', BIRTHDAY_COUNTDOWN: '🎂',
}

function EffectPreview({ effects }: { effects: MagnumEventEffect[] }) {
  const [previewing, setPreviewing] = useState<string | null>(null)

  if (effects.length === 0) return <span className="text-muted-foreground">—</span>

  return (
    <div className="flex flex-wrap gap-1">
      {effects.map(fx => (
        <span
          key={fx.id}
          className={cn(
            'bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 text-xs cursor-pointer hover:bg-amber-200 transition',
            previewing === fx.effectType && 'ring-2 ring-amber-400'
          )}
          onClick={() => setPreviewing(previewing === fx.effectType ? null : fx.effectType)}
          title={`${fx.effectType}${fx.config ? ` — ${fx.config}` : ''}`}
        >
          {EFFECT_ICONS[fx.effectType] || '✨'} {fx.effectType}
        </span>
      ))}
      {previewing && (
        <div className="w-full text-xs text-amber-700 mt-1 italic">
          Preview: {previewing.toLowerCase().replace(/_/g, ' ')}
          {effects.find(e => e.effectType === previewing)?.config && (
            <span className="text-amber-500 ml-1">
              ({effects.find(e => e.effectType === previewing)!.config})
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function InlineEventRow({ event, onUpdate, onDelete, onRefresh, setError }: {
  event: ScoredEvent
  onUpdate: (id: number, updates: Record<string, any>) => void
  onDelete: (id: number) => void
  onRefresh: () => void
  setError: (e: string) => void
}) {
  const [showFacetEdit, setShowFacetEdit] = useState(false)
  const [editFacets, setEditFacets] = useState<FacetValue[]>(event.facets)

  const prevEvents = getPrevEvents(event.facets)
  const otherFacets = event.facets.filter(f => f.facetKey !== 'event' && !f.facetKey.startsWith('prev_event_'))
  const specOpacity = Math.min(event.specificity * 0.07, 0.35)

  const saveFacets = async () => {
    try {
      await apiFetch(`/admin/magnum/events/${event.id}`, {
        method: 'PUT',
        body: JSON.stringify({ facets: editFacets }),
      })
      setShowFacetEdit(false)
      onRefresh()
    } catch (e: any) {
      setError(e.message)
    }
  }

  return (
    <>
      <tr className="border-b hover:bg-muted/30" style={{ backgroundColor: specOpacity > 0 ? `rgba(99, 102, 241, ${specOpacity})` : undefined }}>
        {/* Slug */}
        <td className="px-3 py-1.5">
          <InlineInput
            value={event.slug}
            onChange={v => onUpdate(event.id, { slug: v })}
            className="font-medium text-sm"
          />
        </td>
        {/* Conditions: facets + prev stacked */}
        <td className="px-3 py-1.5">
          <div className="space-y-1">
            <div className="flex items-center gap-1 flex-wrap">
              <FacetPills facets={otherFacets} />
              <button onClick={() => { setEditFacets(event.facets); setShowFacetEdit(!showFacetEdit) }}
                className="text-xs text-blue-500 hover:text-blue-700" title="Edit facets">
                {showFacetEdit ? '✕' : '✎'}
              </button>
            </div>
            {prevEvents.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {prevEvents.map((p, i) => (
                  <span key={i} className="bg-violet-100 text-violet-700 rounded px-1 py-0.5 text-xs">↩ {p}</span>
                ))}
              </div>
            )}
          </div>
        </td>
        {/* Probability: base (editable) + effective + normalized % */}
        <td className="px-3 py-1.5 tabular-nums">
          <div className="flex items-baseline gap-1.5">
            <InlineInput
              value={event.baseProbability.toFixed(3)}
              onChange={v => onUpdate(event.id, { probability: parseFloat(v) || 0.25 })}
              type="number"
              className="w-16"
            />
            {event.specificity > 1 && (
              <span className="text-xs text-indigo-500" title={`${event.specificity} facets → ${Math.pow(2, event.specificity - 1)}× boost`}>
                →{event.effectiveProbability.toFixed(3)}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {(event.normalizedProbability * 100).toFixed(1)}%
            </span>
          </div>
        </td>
        {/* Limits: cooldown + max stacked */}
        <td className="px-3 py-1.5 tabular-nums text-xs">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">CD</span>
              <InlineInput
                value={event.cooldownMs > 0 ? String(event.cooldownMs) : ''}
                onChange={v => onUpdate(event.id, { cooldownMs: parseInt(v) || 0 })}
                type="number"
                placeholder="—"
                className="w-14"
              />
              {event.cooldownMs > 0 && <span className="text-muted-foreground">{formatCooldown(event.cooldownMs)}</span>}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Max</span>
              <InlineInput
                value={event.maxDeliveries != null ? String(event.maxDeliveries) : ''}
                onChange={v => onUpdate(event.id, { maxDeliveries: v ? parseInt(v) : null })}
                type="number"
                placeholder="∞"
                className="w-14"
              />
            </div>
          </div>
        </td>
        {/* Effects with preview */}
        <td className="px-3 py-1.5">
          <EffectPreview effects={event.effects} />
        </td>
        {/* Dialogue */}
        <td className="px-3 py-1.5">
          <InlineTextarea
            value={event.dialogue ?? ''}
            onChange={v => onUpdate(event.id, { dialogue: v.trim() || null })}
            placeholder="(no dialogue)"
          />
        </td>
        {/* Actions */}
        <td className="px-3 py-1.5">
          <button
            onClick={() => { if (confirm(`Delete "${event.slug}"?`)) onDelete(event.id) }}
            className="text-xs text-red-400 hover:text-red-600"
            title="Delete"
          >del</button>
        </td>
      </tr>
      {showFacetEdit && (
        <tr>
          <td colSpan={7} className="bg-muted/20 px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <FacetEditor facets={editFacets} onChange={setEditFacets} compact />
              </div>
              <div className="flex gap-1">
                <button onClick={saveFacets}
                  className="px-3 py-1 bg-primary text-primary-foreground rounded text-xs">Save</button>
                <button onClick={() => setShowFacetEdit(false)}
                  className="px-3 py-1 text-xs text-muted-foreground">Cancel</button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function CreateEventForm({ contextValues, onCreated, onCancel, setError }: {
  contextValues: Record<string, string>; onCreated: () => void; onCancel: () => void; setError: (e: string) => void
}) {
  const [slug, setSlug] = useState('')
  const [probability, setProbability] = useState('0.25')
  const [cooldownMs, setCooldownMs] = useState('0')
  const [maxDeliveries, setMaxDeliveries] = useState('')
  const [facets, setFacets] = useState<FacetValue[]>(() =>
    Object.entries(contextValues)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => ({ facetKey: k, operator: 'EQ', facetValue: v }))
  )
  const [dialogue, setDialogue] = useState('')
  const [effects, setEffects] = useState<{ effectType: string; name: string; config: string }[]>([])
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!slug.trim()) { setError('Slug is required'); return }
    setSaving(true)
    try {
      await apiFetch('/admin/magnum/events', {
        method: 'POST',
        body: JSON.stringify({
          slug: slug.trim(),
          dialogue: dialogue.trim() || null,
          probability: parseFloat(probability) || 0.25,
          cooldownMs: parseInt(cooldownMs) || 0,
          maxDeliveries: maxDeliveries ? parseInt(maxDeliveries) : null,
          facets,
          effects: effects.filter(e => e.effectType).map((e, i) => ({ ...e, sortOrder: i })),
        }),
      })
      onCreated()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-card border rounded-lg p-4 space-y-3">
      <h3 className="font-semibold text-sm">Create New Event</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Slug *</label>
          <input value={slug} onChange={e => setSlug(e.target.value)}
            className="w-full rounded border px-2 py-1 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Dialogue</label>
          <input value={dialogue} onChange={e => setDialogue(e.target.value)}
            placeholder="What Tim says..."
            className="w-full rounded border px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Probability</label>
          <input type="number" step="0.01" value={probability} onChange={e => setProbability(e.target.value)}
            className="w-full rounded border px-2 py-1 text-sm" />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Cooldown</label>
            <input type="number" value={cooldownMs} onChange={e => setCooldownMs(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Max</label>
            <input type="number" value={maxDeliveries} onChange={e => setMaxDeliveries(e.target.value)}
              placeholder="inf" className="w-full rounded border px-2 py-1 text-sm" />
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Facets</label>
        <FacetEditor facets={facets} onChange={setFacets} compact />
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Effects</label>
        {effects.map((fx, i) => (
          <div key={i} className="flex gap-2 mb-1">
            <select value={fx.effectType} onChange={e => {
              const updated = [...effects]; updated[i] = { ...fx, effectType: e.target.value }; setEffects(updated)
            }} className="rounded border px-2 py-1 text-sm bg-white">
              <option value="">Select...</option>
              {EFFECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={fx.name} onChange={e => {
              const updated = [...effects]; updated[i] = { ...fx, name: e.target.value }; setEffects(updated)
            }} placeholder="Name" className="rounded border px-2 py-1 text-sm w-32" />
            <input value={fx.config} onChange={e => {
              const updated = [...effects]; updated[i] = { ...fx, config: e.target.value }; setEffects(updated)
            }} placeholder='{"duration":3000}' className="flex-1 rounded border px-2 py-1 text-sm" />
            <button onClick={() => setEffects(effects.filter((_, j) => j !== i))} className="text-red-500 text-sm">x</button>
          </div>
        ))}
        <button onClick={() => setEffects([...effects, { effectType: '', name: '', config: '' }])}
          className="text-xs text-blue-600">+ Add effect</button>
      </div>

      <div className="flex gap-2">
        <button onClick={submit} disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded text-sm font-medium disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Event'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-muted-foreground">Cancel</button>
      </div>
    </div>
  )
}

// ─── Personality Studio ───────────────────────────────────────────

// ─── Playground ──────────────────────────────────────────────────

interface PlaygroundWord {
  id: number; text: string; gradeLevel: number
}
interface PlaygroundReaction {
  id: number; slug: string; dialogue: string | null; effects: MagnumEventEffect[]
  baseProbability: number; effectiveProbability: number; normalizedProbability: number
  facets: FacetValue[]; specificity: number; cooldownMs: number; maxDeliveries: number | null
}

function PlaygroundTab({ setError }: { setError: (e: string) => void }) {
  const [age, setAge] = useState(10)
  const [gradeLevel, setGradeLevel] = useState(3.0)
  const [exerciseType, setExerciseType] = useState('WORD_DECODE')

  const [words, setWords] = useState<PlaygroundWord[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [loading, setLoading] = useState(false)

  const [streak, setStreak] = useState(0)
  const [sessionCorrect, setSessionCorrect] = useState(0)
  const [sessionTotal, setSessionTotal] = useState(0)
  const [wordStartTime, setWordStartTime] = useState(0)

  const [lastReaction, setLastReaction] = useState<PlaygroundReaction | null>(null)
  const [allMatchingReactions, setAllMatchingReactions] = useState<PlaygroundReaction[]>([])
  const [nothingProbability, setNothingProbability] = useState(0)
  const [lastContextMap, setLastContextMap] = useState<Record<string, string>>({})
  const [lastResult, setLastResult] = useState<{ correct: boolean; word: string } | null>(null)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  const [prevReactions, setPrevReactions] = useState<string[]>([])

  const currentWord = words[currentIndex] || null

  const startGame = async () => {
    setLoading(true)
    setError('')
    try {
      const grade = Math.floor(gradeLevel)
      const res = await apiFetch<PlaygroundWord[]>(`/words?gradeLevel=${grade}`)
      const shuffled = [...res].sort(() => Math.random() - 0.5).slice(0, 10)
      setWords(shuffled)
      setCurrentIndex(0)
      setStreak(0)
      setSessionCorrect(0)
      setSessionTotal(0)
      setLastReaction(null)
      setAllMatchingReactions([])
      setLastResult(null)
      setPrevReactions([])
      setPlaying(true)
      setWordStartTime(Date.now())

      await evaluateReaction('SESSION_START', null, null, 0, 0)
    } catch (e: any) {
      setError(e.message || 'Failed to start game')
    } finally {
      setLoading(false)
    }
  }

  const evaluateReaction = async (
    contextType: string,
    word: PlaygroundWord | null,
    correct: boolean | null,
    currentStreak: number,
    responseMs: number,
  ) => {
    const contextMap: Record<string, string> = {
      event: contextType,
      activity: exerciseType,
      age: String(age),
      grade_level: String(gradeLevel),
    }
    if (currentStreak > 0) contextMap.streak = String(currentStreak)
    if (word) contextMap.word_text = word.text
    if (correct !== null) contextMap.correct = String(correct)
    if (responseMs > 0) contextMap.response_ms = String(responseMs)

    prevReactions.forEach((slug, i) => {
      contextMap[`prev_event_${i + 1}`] = slug
    })

    setLastContextMap(contextMap)

    try {
      const res = await apiFetch<DetailedTestResult>('/admin/magnum/engine/test-detailed', {
        method: 'POST',
        body: JSON.stringify(contextMap),
      })
      setAllMatchingReactions(res.matchingEvents)
      setNothingProbability(res.nothingProbability)

      // Simulate probabilistic selection
      const roll = Math.random()
      let cumulative = 0
      let selected: PlaygroundReaction | null = null
      for (const evt of res.matchingEvents) {
        cumulative += evt.normalizedProbability
        if (roll < cumulative) {
          selected = evt
          break
        }
      }
      setLastReaction(selected)

      if (selected) {
        setPrevReactions(prev => [String(selected!.id), ...prev].slice(0, 3))
        if (selected.effects?.length) {
          setActiveEffects(selected.effects.map(e => ({
            effectType: e.effectType, name: e.name, config: e.config
          })))
        }
      }
    } catch (e: any) {
      setError(e.message)
    }
  }

  const handleAnswer = async (correct: boolean) => {
    if (!currentWord) return
    const responseMs = Date.now() - wordStartTime
    const newStreak = correct ? streak + 1 : 0
    const newCorrect = correct ? sessionCorrect + 1 : sessionCorrect
    const newTotal = sessionTotal + 1

    setStreak(newStreak)
    setSessionCorrect(newCorrect)
    setSessionTotal(newTotal)
    setLastResult({ correct, word: currentWord.text })

    const isMilestone = correct && [3, 5, 7, 10].includes(newStreak)
    const contextType = correct
      ? (isMilestone ? 'STREAK_MILESTONE' : 'CORRECT_ANSWER')
      : 'WRONG_ANSWER'

    await evaluateReaction(contextType, currentWord, correct, newStreak, responseMs)
  }

  const nextWord = () => {
    const next = currentIndex + 1
    if (next >= words.length) {
      evaluateReaction('SESSION_END', null, null, streak, 0)
      setPlaying(false)
    } else {
      setCurrentIndex(next)
      setLastResult(null)
      setLastReaction(null)
      setWordStartTime(Date.now())
    }
  }

  const updateReaction = async (reactionId: number, updates: Record<string, any>) => {
    try {
      await apiFetch(`/admin/magnum/events/${reactionId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
      setError('')
    } catch (e: any) {
      setError(e.message)
    }
  }

  const accuracy = sessionTotal > 0 ? Math.round((sessionCorrect / sessionTotal) * 100) : 0

  return (
    <div className="space-y-4">
      {/* Admin Controls */}
      <div className="bg-card border rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Simulated Student</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-muted-foreground">Age</label>
            <div className="flex items-center gap-2">
              <input type="range" min={6} max={18} value={age} onChange={e => setAge(parseInt(e.target.value))}
                className="flex-1" />
              <span className="text-sm font-medium w-6 text-right">{age}</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Grade Level</label>
            <div className="flex items-center gap-2">
              <input type="range" min={1} max={5} step={0.1} value={gradeLevel}
                onChange={e => setGradeLevel(parseFloat(e.target.value))} className="flex-1" />
              <span className="text-sm font-medium w-8 text-right">{gradeLevel.toFixed(1)}</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Exercise</label>
            <select value={exerciseType} onChange={e => setExerciseType(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm bg-white">
              <option value="WORD_DECODE">Word Decode</option>
              <option value="SYNONYM_MATCH">Synonym Match</option>
              <option value="SYNONYM_PAIRS">Synonym Pairs</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={startGame} disabled={loading}
              className="px-4 py-1.5 bg-primary text-primary-foreground rounded text-sm font-medium disabled:opacity-50">
              {loading ? 'Loading...' : playing ? 'Restart' : 'Start Game'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inner: Game Screen */}
        <div className="bg-card border rounded-lg overflow-hidden">
          <div className="bg-muted/30 px-3 py-1.5 border-b flex items-center gap-2 text-xs text-muted-foreground">
            <span>Game Preview</span>
            {playing && (
              <>
                <span className="ml-auto">{currentIndex + 1}/{words.length}</span>
                <span>{sessionCorrect} correct</span>
                <span>{accuracy}%</span>
                {streak >= 3 && <span className="text-amber-500 font-medium">streak {streak}</span>}
              </>
            )}
          </div>
          <div className="relative min-h-[300px] flex items-center justify-center p-8">
            <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

            {!playing && words.length === 0 && (
              <div className="text-center text-muted-foreground">
                <p className="text-lg mb-2">Set student params and press Start</p>
                <p className="text-sm">You'll play the game as a student would see it, with admin controls alongside</p>
              </div>
            )}

            {!playing && words.length > 0 && (
              <div className="text-center space-y-4">
                <div className="text-5xl">🎉</div>
                <p className="text-xl font-bold">Session Complete!</p>
                <p className="text-muted-foreground">{sessionCorrect}/{sessionTotal} correct ({accuracy}%)</p>
                <button onClick={startGame} className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium">
                  Play Again
                </button>
              </div>
            )}

            {playing && currentWord && (
              <div className="text-center space-y-6 w-full">
                {/* Progress bar */}
                <div className="w-full bg-accent rounded-full h-1.5">
                  <div className="bg-primary h-1.5 rounded-full transition-all"
                    style={{ width: `${((currentIndex + (lastResult ? 1 : 0)) / words.length) * 100}%` }} />
                </div>

                {/* Word display */}
                <p className="text-6xl font-black tracking-tight">{currentWord.text}</p>
                <p className="text-xs text-muted-foreground">Grade {currentWord.gradeLevel}</p>

                {/* Result */}
                {lastResult && (
                  <div className={cn('text-lg font-semibold', lastResult.correct ? 'text-green-600' : 'text-amber-500')}>
                    {lastResult.correct ? '✓ Correct!' : '✗ Wrong'}
                  </div>
                )}

                {/* Action buttons */}
                {!lastResult ? (
                  <div className="flex gap-3 justify-center">
                    <button onClick={() => handleAnswer(true)}
                      className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition">
                      ✓ Correct
                    </button>
                    <button onClick={() => handleAnswer(false)}
                      className="px-6 py-3 bg-red-500 text-white rounded-xl font-bold text-lg hover:bg-red-600 transition">
                      ✗ Wrong
                    </button>
                  </div>
                ) : (
                  <button onClick={nextWord}
                    className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition">
                    {currentIndex + 1 >= words.length ? 'Finish' : 'Next Word →'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Outer: Admin Panel */}
        <div className="space-y-3">
          {/* Live Context */}
          <div className="bg-card border rounded-lg p-3">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">Live Context</h4>
            <div className="flex flex-wrap gap-1">
              {Object.entries(lastContextMap).map(([k, v]) => (
                <span key={k} className="bg-muted rounded px-1.5 py-0.5 text-xs">
                  <span className="text-muted-foreground">{k}=</span>{v}
                </span>
              ))}
              {Object.keys(lastContextMap).length === 0 && (
                <span className="text-xs text-muted-foreground">Start a game to see context</span>
              )}
            </div>
          </div>

          {/* Fired Reaction */}
          {lastReaction && (
            <div className="bg-card border-2 border-indigo-300 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-xs font-semibold text-indigo-600">Fired Reaction</h4>
                <span className="text-xs text-muted-foreground ml-auto">
                  {(lastReaction.normalizedProbability * 100).toFixed(1)}% chance
                </span>
              </div>
              <div className="font-medium text-sm">{lastReaction.slug}</div>
              {lastReaction.dialogue && (
                <div className="bg-muted/50 rounded p-2 text-sm italic">"{lastReaction.dialogue}"</div>
              )}
              {lastReaction.effects.length > 0 && (
                <div className="flex gap-1">
                  {lastReaction.effects.map(fx => (
                    <span key={fx.id} className="bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 text-xs">
                      {EFFECT_ICONS[fx.effectType] || '✨'} {fx.effectType}
                    </span>
                  ))}
                </div>
              )}
              <FacetPills facets={lastReaction.facets} />
              <div className="flex gap-2 pt-1">
                <button onClick={() => {
                  const tab = document.querySelector('[data-tab="explorer"]') as HTMLElement
                  if (tab) tab.click()
                }}
                  className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-200">
                  Edit in Explorer
                </button>
                <button onClick={() => setShowCreateForm(true)}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">
                  + Create Reaction
                </button>
              </div>
            </div>
          )}

          {/* Nothing fired */}
          {!lastReaction && Object.keys(lastContextMap).length > 0 && (
            <div className="bg-card border border-dashed rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <h4 className="text-xs font-semibold text-muted-foreground">No Reaction Fired</h4>
                <span className="text-xs text-muted-foreground ml-auto">
                  {(nothingProbability * 100).toFixed(1)}% nothing chance
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {allMatchingReactions.length} reactions matched but the roll landed on "nothing"
              </p>
              <button onClick={() => setShowCreateForm(true)}
                className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200">
                + Create Reaction for this context
              </button>
            </div>
          )}

          {/* Create form */}
          {showCreateForm && (
            <PlaygroundCreateForm
              contextMap={lastContextMap}
              onCreated={() => setShowCreateForm(false)}
              onCancel={() => setShowCreateForm(false)}
              setError={setError}
            />
          )}

          {/* All matching reactions */}
          {allMatchingReactions.length > 0 && (
            <div className="bg-card border rounded-lg p-3">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">
                All Matching Reactions ({allMatchingReactions.length})
              </h4>
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {allMatchingReactions.map(evt => (
                  <div key={evt.id}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1 rounded text-xs',
                      lastReaction?.id === evt.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-muted/30'
                    )}>
                    <span className="font-medium flex-1 truncate">{evt.slug}</span>
                    {evt.effects.length > 0 && (
                      <span className="text-amber-600">{evt.effects.map(e => EFFECT_ICONS[e.effectType] || '✨').join('')}</span>
                    )}
                    <span className="tabular-nums text-muted-foreground w-12 text-right">
                      {(evt.normalizedProbability * 100).toFixed(1)}%
                    </span>
                    {evt.dialogue && (
                      <span className="text-muted-foreground truncate max-w-[120px]" title={evt.dialogue}>
                        {evt.dialogue}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PlaygroundCreateForm({ contextMap, onCreated, onCancel, setError }: {
  contextMap: Record<string, string>; onCreated: () => void; onCancel: () => void; setError: (e: string) => void
}) {
  const [slug, setSlug] = useState('')
  const [dialogue, setDialogue] = useState('')
  const [probability, setProbability] = useState('0.25')
  const [facets, setFacets] = useState<FacetValue[]>(() =>
    Object.entries(contextMap)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => ({ facetKey: k, operator: 'EQ', facetValue: v }))
  )
  const [effects, setEffects] = useState<{ effectType: string; name: string; config: string }[]>([])
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!slug.trim()) { setError('Slug is required'); return }
    setSaving(true)
    try {
      await apiFetch('/admin/magnum/events', {
        method: 'POST',
        body: JSON.stringify({
          slug: slug.trim(),
          dialogue: dialogue.trim() || null,
          probability: parseFloat(probability) || 0.25,
          cooldownMs: 0,
          maxDeliveries: null,
          facets,
          effects: effects.filter(e => e.effectType).map((e, i) => ({ ...e, sortOrder: i })),
        }),
      })
      onCreated()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bg-card border-2 border-green-300 rounded-lg p-3 space-y-2">
      <h4 className="text-xs font-semibold text-green-700">Create Reaction from Context</h4>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">Slug *</label>
          <input value={slug} onChange={e => setSlug(e.target.value)}
            className="w-full rounded border px-2 py-1 text-sm" placeholder="my-reaction-slug" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Probability</label>
          <input type="number" step="0.01" value={probability} onChange={e => setProbability(e.target.value)}
            className="w-full rounded border px-2 py-1 text-sm" />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Dialogue</label>
        <input value={dialogue} onChange={e => setDialogue(e.target.value)}
          placeholder="What Tim says..." className="w-full rounded border px-2 py-1 text-sm" />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Facets (pre-filled from context)</label>
        <FacetEditor facets={facets} onChange={setFacets} compact />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Effects</label>
        {effects.map((fx, i) => (
          <div key={i} className="flex gap-1 mb-1">
            <select value={fx.effectType} onChange={e => {
              const updated = [...effects]; updated[i] = { ...fx, effectType: e.target.value }; setEffects(updated)
            }} className="rounded border px-1 py-0.5 text-xs bg-white">
              <option value="">Type...</option>
              {EFFECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => setEffects(effects.filter((_, j) => j !== i))} className="text-red-500 text-xs">x</button>
          </div>
        ))}
        <button onClick={() => setEffects([...effects, { effectType: '', name: '', config: '' }])}
          className="text-xs text-blue-600">+ effect</button>
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving}
          className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium disabled:opacity-50">
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button onClick={onCancel} className="px-3 py-1 text-xs text-muted-foreground">Cancel</button>
      </div>
    </div>
  )
}

// ─── Personality Studio ───────────────────────────────────────────

function PersonalityStudioTab({ setError }: { setError: (e: string) => void }) {
  const [prompt, setPrompt] = useState<PersonalityPrompt | null>(null)
  const [history, setHistory] = useState<PersonalityPrompt[]>([])
  const [editPrompt, setEditPrompt] = useState('')
  const [saving, setSaving] = useState(false)

  const [genContext, setGenContext] = useState<FacetValue[]>([{ facetKey: 'event', operator: 'EQ', facetValue: 'CORRECT_ANSWER' }])
  const [genEventId, setGenEventId] = useState('')
  const [genCount, setGenCount] = useState(5)
  const [generating, setGenerating] = useState(false)
  const [generatedLines, setGeneratedLines] = useState<{ eventId: number; text: string }[]>([])

  const [pending, setPending] = useState<any[]>([])

  useEffect(() => {
    apiFetch<PersonalityPrompt>('/admin/magnum/personality').then(p => {
      setPrompt(p); setEditPrompt(p.systemPrompt)
    }).catch(() => {})
    apiFetch<PersonalityPrompt[]>('/admin/magnum/personality/history').then(setHistory).catch(() => {})
    loadPending()
  }, [])

  const loadPending = () => {
    apiFetch<any[]>('/admin/magnum/generate/pending').then(setPending).catch(() => {})
  }

  const savePrompt = async () => {
    setSaving(true)
    try {
      const p = await apiFetch<PersonalityPrompt>('/admin/magnum/personality', {
        method: 'POST', body: JSON.stringify({ systemPrompt: editPrompt })
      })
      setPrompt(p)
      const h = await apiFetch<PersonalityPrompt[]>('/admin/magnum/personality/history')
      setHistory(h)
    } catch (e: any) { setError(e.message) }
    finally { setSaving(false) }
  }

  const generate = async () => {
    setGenerating(true)
    setGeneratedLines([])
    try {
      const context: Record<string, string> = {}
      for (const f of genContext) {
        if (f.facetKey && f.facetValue) context[f.facetKey] = f.facetValue
      }
      const res = await apiFetch<{ lines: { eventId: number; text: string }[] }>('/admin/magnum/generate', {
        method: 'POST',
        body: JSON.stringify({ context, count: genCount, eventId: genEventId ? parseInt(genEventId) : undefined }),
      })
      setGeneratedLines(res.lines)
      loadPending()
    } catch (e: any) { setError(e.message) }
    finally { setGenerating(false) }
  }

  const review = async (eventId: number, status: string, editedText?: string) => {
    try {
      await apiFetch(`/admin/magnum/generate/${eventId}/review`, {
        method: 'POST', body: JSON.stringify({ status, editedText })
      })
      loadPending()
      setGeneratedLines(prev => prev.filter(l => l.eventId !== eventId))
    } catch (e: any) { setError(e.message) }
  }

  return (
    <div className="space-y-6">
      {/* Prompt Editor */}
      <div className="bg-card border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Personality Prompt</h3>
          {prompt && <span className="text-xs text-muted-foreground">v{prompt.version}</span>}
        </div>
        <textarea value={editPrompt} onChange={e => setEditPrompt(e.target.value)}
          rows={6} className="w-full rounded border px-3 py-2 text-sm font-mono" />
        <div className="flex gap-2">
          <button onClick={savePrompt} disabled={saving}
            className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Save New Version'}
          </button>
          {history.length > 1 && (
            <select onChange={e => {
              const h = history.find(h => h.id === parseInt(e.target.value))
              if (h) setEditPrompt(h.systemPrompt)
            }} className="rounded border px-2 py-1 text-sm bg-white">
              <option value="">History...</option>
              {history.map(h => <option key={h.id} value={h.id}>v{h.version}{h.isActive ? ' (active)' : ''}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Generator */}
      <div className="bg-card border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-sm">Generate Lines</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Context</label>
            <FacetEditor facets={genContext} onChange={setGenContext} compact />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Event ID (optional)</label>
            <input type="number" value={genEventId} onChange={e => setGenEventId(e.target.value)}
              placeholder="auto" className="w-24 rounded border px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Count</label>
            <input type="number" min={1} max={10} value={genCount} onChange={e => setGenCount(parseInt(e.target.value) || 5)}
              className="w-16 rounded border px-2 py-1 text-sm" />
          </div>
          <button onClick={generate} disabled={generating}
            className="px-4 py-2 bg-purple-600 text-white rounded text-sm font-medium disabled:opacity-50">
            {generating ? 'Generating...' : 'Generate'}
          </button>
        </div>

        {generatedLines.length > 0 && (
          <div className="space-y-2 mt-3">
            <h4 className="text-xs font-semibold text-muted-foreground">Generated</h4>
            {generatedLines.map(line => (
              <ReviewCard key={line.eventId} eventId={line.eventId} text={line.text} onReview={review} />
            ))}
          </div>
        )}
      </div>

      {/* Pending Reviews */}
      {pending.length > 0 && (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-sm">Pending Reviews ({pending.length})</h3>
          {pending.map((r: any) => (
            <ReviewCard key={r.id} eventId={r.eventId} text={r.contextJson} onReview={review} />
          ))}
        </div>
      )}
    </div>
  )
}

function ReviewCard({ eventId, text, onReview }: {
  eventId: number; text: string; onReview: (eventId: number, status: string, editedText?: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(text)

  return (
    <div className="flex items-center gap-2 p-2 bg-muted/30 rounded">
      {editing ? (
        <input value={editText} onChange={e => setEditText(e.target.value)}
          className="flex-1 rounded border px-2 py-1 text-sm" />
      ) : (
        <span className="flex-1 text-sm">{text}</span>
      )}
      <div className="flex gap-1">
        <button onClick={() => onReview(eventId, 'APPROVED')}
          className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Approve</button>
        {editing ? (
          <button onClick={() => { onReview(eventId, 'EDITED', editText); setEditing(false) }}
            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Save Edit</button>
        ) : (
          <button onClick={() => setEditing(true)}
            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Edit</button>
        )}
        <button onClick={() => onReview(eventId, 'REJECTED')}
          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Reject</button>
      </div>
    </div>
  )
}
